import React from 'react';
import { ListGroup, Button } from 'react-bootstrap';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../services/api';

const DragHandle = ({ listeners }) => (
  <span
    {...listeners}
    style={{
      cursor: 'grab',
      padding: '0 8px',
      fontSize: '1.25rem',
      userSelect: 'none',
    }}
    title="Drag to reorder"
  >
    ☰
  </span>
);

const SortableItem = ({
  field,
  fields,
  editingFieldId,
  setEditingFieldId,
  handleDeleteField,
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
  } = useSortable({ id: field.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const conditions = Array.isArray(field.condition) ? field.condition : [];
  const conditionBadges = conditions.map((cond, i) => {
    const dependsOn = fields.find(dep => dep.id === cond.dependsOnFieldId);
    if (!dependsOn) return null;
    return (
      <span key={i} className="badge bg-warning text-dark me-1">
        if "{dependsOn.label}" = "{cond.expectedValue}"
      </span>
    );
  });

  const hasDependents = fields.some(
    other =>
      Array.isArray(other.condition) &&
      other.condition.some(cond => cond.dependsOnFieldId === field.id)
  );

  return (
    <ListGroup.Item
      ref={setNodeRef}
      style={style}
      className={`d-flex justify-content-between align-items-start ${
        editingFieldId === field.id ? 'bg-light border border-primary' : ''
      }`}
    >
      <div className="me-3">
        <DragHandle listeners={listeners} />
      </div>

      <div className="flex-grow-1">
        <div className="fw-bold">{field.label}</div>
        <div className="small text-muted">
          Field: <code>{field.name}</code>
        </div>
        <div className="mt-1">
          <span className="badge me-1 bg-secondary text-capitalize">
            {field.type}
          </span>
          {field.required && (
            <span className="badge bg-danger me-1">Required</span>
          )}
          {conditionBadges}
          {hasDependents && (
            <span className="badge bg-info text-dark ms-2">Depended On</span>
          )}
        </div>
      </div>

      <div className="d-flex flex-column gap-1 ms-2">
        <Button
          size="sm"
          variant="outline-danger"
          disabled={hasDependents}
          onClick={() => !hasDependents && handleDeleteField(field.id)}
        >
          🗑 Delete
        </Button>
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => setEditingFieldId(field.id)}
        >
          Edit
        </Button>
      </div>
    </ListGroup.Item>
  );
};

const FieldList = ({
  fields,
  onReorder,
  handleDeleteField,
  editingFieldId,
  setEditingFieldId,
  formId,
  projectId,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id.toString() === active.id.toString());
    const newIndex = fields.findIndex(f => f.id.toString() === over.id.toString());

    if (oldIndex !== -1 && newIndex !== -1) {
      const newFields = arrayMove(fields, oldIndex, newIndex);
      api.put(`/projects/${projectId}/forms/${formId}/fields/reorder`, {
        orderedFieldIds: newFields.map(f => f.id)
      });
      onReorder();
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={fields.map(f => f.id.toString())}
        strategy={verticalListSortingStrategy}
      >
        <ListGroup className="mt-4">
          {fields.map((field) => (
            <SortableItem
              key={field.id}
              field={field}
              fields={fields}
              editingFieldId={editingFieldId}
              setEditingFieldId={setEditingFieldId}
              handleDeleteField={handleDeleteField}
            />
          ))}
        </ListGroup>
      </SortableContext>
    </DndContext>
  );
};

export default FieldList;
